(function () {
  'use strict';

  /*
   * Alliance Garage guest storage contract
   * --------------------------------------
   * Key: alliance.garage.v1
   * Schema version: 1
   * Maximum serialized payload: 256 KiB; maximum active items: 100.
   *
   * {
   *   schema_version: 1,
   *   vehicle: {
   *     client_uuid, nickname, year, make, model, trim, engine, drivetrain,
   *     source: "customer_entered"
   *   },
   *   build: {
   *     client_uuid, name, purpose, goal, budget_amount, budget_currency,
   *     status: "active", draft_note
   *   },
   *   items: [{
   *     client_uuid, category_key, kind, state, priority, quantity, position,
   *     shopify_product_gid, shopify_variant_gid, product_handle_snapshot,
   *     product_url_snapshot, vendor_snapshot, title_snapshot, sku_snapshot,
   *     mpn_snapshot, image_url_snapshot, price_snapshot,
   *     compare_at_price_snapshot, availability_snapshot,
   *     inventory_quantity_snapshot, special_order_snapshot,
   *     availability_checked_at, currency_snapshot,
   *     fitment_state: "not_verified", customer_note, created_at, updated_at
   *   }],
   *   created_at, updated_at
   * }
   *
   * All imported and API-provided values are reconstructed from an allowlist.
   * No stored value is inserted as HTML.
   */

  var STORAGE_KEY = 'alliance.garage.v1';
  var SCHEMA_VERSION = 1;
  var MAX_BYTES = 262144;
  var MAX_ITEMS = 100;
  var VALID_PURPOSES = ['street', 'daily', 'show', 'drag', 'track', 'off_road', 'tow', 'other'];
  var VALID_STATES = ['considering', 'planned', 'ready_to_buy', 'ordered', 'installed', 'needs_support'];
  var VALID_PRIORITIES = ['low', 'normal', 'high'];
  var VALID_KINDS = ['task', 'shopify_variant'];
  var memoryState = null;
  var storageUsable = null;
  var roots = [];

  function now() {
    return new Date().toISOString();
  }

  function uuid() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
    var values = new Uint8Array(16);
    if (window.crypto && typeof window.crypto.getRandomValues === 'function') {
      window.crypto.getRandomValues(values);
    } else {
      for (var index = 0; index < values.length; index += 1) values[index] = Math.floor(Math.random() * 256);
    }
    values[6] = (values[6] & 15) | 64;
    values[8] = (values[8] & 63) | 128;
    var hex = Array.from(values, function (value) { return value.toString(16).padStart(2, '0'); }).join('');
    return hex.slice(0, 8) + '-' + hex.slice(8, 12) + '-' + hex.slice(12, 16) + '-' + hex.slice(16, 20) + '-' + hex.slice(20);
  }

  function isObject(value) {
    return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
  }

  function cleanText(value, maximum) {
    return String(value == null ? '' : value).replace(/\u0000/g, '').trim().slice(0, maximum);
  }

  function cleanKey(value) {
    var key = cleanText(value, 64).toLowerCase().replace(/[^a-z0-9_]+/g, '_').replace(/^_+|_+$/g, '');
    return key || 'uncategorized';
  }

  function cleanInteger(value, minimum, maximum, fallback) {
    var number = Number(value);
    if (!Number.isFinite(number)) return fallback;
    return Math.min(maximum, Math.max(minimum, Math.round(number)));
  }

  function cleanMoney(value) {
    if (value === '' || value == null) return null;
    var number = Number(value);
    if (!Number.isFinite(number) || number < 0 || number > 10000000) return null;
    return Math.round(number * 100) / 100;
  }

  function cleanAvailability(value) {
    var availability = cleanText(value, 20).toLowerCase();
    return availability === 'available' || availability === 'unavailable' ? availability : 'unknown';
  }

  function cleanUuid(value) {
    var candidate = cleanText(value, 80);
    return /^[a-zA-Z0-9_-]{8,80}$/.test(candidate) ? candidate : uuid();
  }

  function cleanGid(value, type) {
    var candidate = cleanText(value, 100);
    if (/^\d{1,30}$/.test(candidate)) return 'gid://shopify/' + type + '/' + candidate;
    var pattern = new RegExp('^gid://shopify/' + type + '/\\d{1,30}$');
    return pattern.test(candidate) ? candidate : '';
  }

  function numericId(value, type) {
    var gid = cleanGid(value, type);
    return gid ? gid.split('/').pop() : '';
  }

  function cleanCurrency(value) {
    var currency = cleanText(value || 'USD', 3).toUpperCase();
    return /^[A-Z]{3}$/.test(currency) ? currency : 'USD';
  }

  function cleanUrl(value, productOnly) {
    var candidate = cleanText(value, 500);
    if (!candidate) return '';
    try {
      var parsed = new URL(candidate, window.location.origin);
      if (parsed.origin !== window.location.origin) {
        if (productOnly) return '';
        var approvedImageHost = parsed.hostname === 'cdn.shopify.com' ||
          parsed.hostname.endsWith('.cdn.shopify.com') ||
          parsed.hostname === 'cdn.shopifycdn.net' ||
          parsed.hostname.endsWith('.cdn.shopifycdn.net') ||
          parsed.hostname === 'cdn.shopifycloud.com' ||
          parsed.hostname.endsWith('.cdn.shopifycloud.com');
        if (parsed.protocol !== 'https:' || !approvedImageHost) return '';
      }
      if (productOnly && !/^\/products\/[a-z0-9][a-z0-9-]*(?:\/|$)/i.test(parsed.pathname)) return '';
      if (productOnly) return parsed.pathname + parsed.search;
      return parsed.href;
    } catch (_) {
      return '';
    }
  }

  function productHandleFrom(value, fallback) {
    var explicit = cleanText(fallback, 255).toLowerCase();
    if (/^[a-z0-9][a-z0-9-]*$/.test(explicit)) return explicit;
    var url = cleanUrl(value, true);
    var match = url.match(/^\/products\/([a-z0-9][a-z0-9-]*)/i);
    return match ? match[1].toLowerCase() : '';
  }

  function defaultState() {
    var stamp = now();
    return {
      schema_version: SCHEMA_VERSION,
      vehicle: {
        client_uuid: uuid(),
        nickname: '',
        year: null,
        make: '',
        model: '',
        trim: '',
        engine: '',
        drivetrain: '',
        source: 'customer_entered'
      },
      build: {
        client_uuid: uuid(),
        name: 'Street build',
        purpose: 'street',
        goal: '',
        budget_amount: null,
        budget_currency: 'USD',
        status: 'active',
        draft_note: ''
      },
      items: [],
      created_at: stamp,
      updated_at: stamp
    };
  }

  function validateVehicle(raw) {
    raw = isObject(raw) ? raw : {};
    var year = raw.year === '' || raw.year == null ? null : cleanInteger(raw.year, 1886, 2100, null);
    return {
      client_uuid: cleanUuid(raw.client_uuid),
      nickname: cleanText(raw.nickname, 80),
      year: year,
      make: cleanText(raw.make, 80),
      model: cleanText(raw.model, 80),
      trim: cleanText(raw.trim, 80),
      engine: cleanText(raw.engine, 80),
      drivetrain: cleanText(raw.drivetrain, 80),
      source: 'customer_entered'
    };
  }

  function validateBuild(raw) {
    raw = isObject(raw) ? raw : {};
    var purpose = cleanText(raw.purpose, 20);
    return {
      client_uuid: cleanUuid(raw.client_uuid),
      name: cleanText(raw.name || 'Street build', 100) || 'Street build',
      purpose: VALID_PURPOSES.indexOf(purpose) > -1 ? purpose : 'other',
      goal: cleanText(raw.goal, 500),
      budget_amount: cleanMoney(raw.budget_amount),
      budget_currency: cleanCurrency(raw.budget_currency),
      status: 'active',
      draft_note: cleanText(raw.draft_note, 2000)
    };
  }

  function validateItem(raw, index) {
    if (!isObject(raw)) return null;
    var kind = cleanText(raw.kind, 30);
    var state = cleanText(raw.state, 30);
    var priority = cleanText(raw.priority, 20);
    var variantGid = cleanGid(raw.shopify_variant_gid || raw.variant_id, 'ProductVariant');
    var productGid = cleanGid(raw.shopify_product_gid || raw.product_id, 'Product');
    var title = cleanText(raw.title_snapshot || raw.title, 160);
    if (!title) return null;
    if (kind === 'shopify_variant' && !variantGid) return null;
    if (VALID_KINDS.indexOf(kind) === -1) kind = variantGid ? 'shopify_variant' : 'task';
    return {
      client_uuid: cleanUuid(raw.client_uuid),
      category_key: cleanKey(raw.category_key),
      kind: kind,
      state: VALID_STATES.indexOf(state) > -1 ? state : 'considering',
      priority: VALID_PRIORITIES.indexOf(priority) > -1 ? priority : 'normal',
      quantity: cleanInteger(raw.quantity, 1, 99, 1),
      position: cleanInteger(raw.position, 0, MAX_ITEMS * 100, index),
      shopify_product_gid: productGid || null,
      shopify_variant_gid: variantGid || null,
      product_handle_snapshot: productHandleFrom(raw.product_url_snapshot || raw.product_url, raw.product_handle_snapshot || raw.product_handle),
      product_url_snapshot: cleanUrl(raw.product_url_snapshot || raw.product_url, true),
      vendor_snapshot: cleanText(raw.vendor_snapshot || raw.vendor, 100),
      title_snapshot: title,
      sku_snapshot: cleanText(raw.sku_snapshot || raw.sku, 100),
      mpn_snapshot: cleanText(raw.mpn_snapshot || raw.mpn, 100),
      image_url_snapshot: cleanUrl(raw.image_url_snapshot || raw.image_url, false),
      price_snapshot: cleanMoney(raw.price_snapshot == null ? raw.price : raw.price_snapshot),
      compare_at_price_snapshot: cleanMoney(raw.compare_at_price_snapshot == null ? raw.compare_at_price : raw.compare_at_price_snapshot),
      availability_snapshot: cleanAvailability(raw.availability_snapshot || raw.availability),
      inventory_quantity_snapshot: raw.inventory_quantity_snapshot == null && raw.inventory_quantity == null
        ? null
        : cleanInteger(raw.inventory_quantity_snapshot == null ? raw.inventory_quantity : raw.inventory_quantity_snapshot, -1000000, 1000000, null),
      special_order_snapshot: raw.special_order_snapshot === true || raw.special_order === true,
      availability_checked_at: cleanText(raw.availability_checked_at, 40),
      currency_snapshot: cleanCurrency(raw.currency_snapshot || raw.currency),
      fitment_state: 'not_verified',
      customer_note: cleanText(raw.customer_note || raw.note, 1000),
      created_at: cleanText(raw.created_at, 40) || now(),
      updated_at: cleanText(raw.updated_at, 40) || now()
    };
  }

  function validateState(raw) {
    if (!isObject(raw) || Number(raw.schema_version) !== SCHEMA_VERSION) {
      throw new Error('UNKNOWN_SCHEMA_VERSION');
    }
    var serialized = JSON.stringify(raw);
    if (new Blob([serialized]).size > MAX_BYTES) throw new Error('PAYLOAD_TOO_LARGE');
    if (!Array.isArray(raw.items) || raw.items.length > MAX_ITEMS) throw new Error('TOO_MANY_ITEMS');
    var state = {
      schema_version: SCHEMA_VERSION,
      vehicle: validateVehicle(raw.vehicle),
      build: validateBuild(raw.build),
      items: raw.items.map(validateItem).filter(Boolean),
      created_at: cleanText(raw.created_at, 40) || now(),
      updated_at: cleanText(raw.updated_at, 40) || now()
    };
    if (state.items.length !== raw.items.length) throw new Error('INVALID_ITEM');
    return state;
  }

  function canUseStorage() {
    if (storageUsable !== null) return storageUsable;
    try {
      var testKey = STORAGE_KEY + '.storage-test';
      window.localStorage.setItem(testKey, '1');
      window.localStorage.removeItem(testKey);
      storageUsable = true;
    } catch (_) {
      storageUsable = false;
    }
    return storageUsable;
  }

  function readState() {
    if (memoryState) return memoryState;
    if (!canUseStorage()) {
      memoryState = defaultState();
      return memoryState;
    }
    var raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      memoryState = defaultState();
      return memoryState;
    }
    try {
      memoryState = validateState(JSON.parse(raw));
    } catch (_) {
      memoryState = defaultState();
      memoryState.storage_error = 'The saved local Garage could not be read. It was not imported.';
    }
    return memoryState;
  }

  function writeState(state) {
    state.updated_at = now();
    var valid = validateState(state);
    var serialized = JSON.stringify(valid);
    if (new Blob([serialized]).size > MAX_BYTES) throw new Error('PAYLOAD_TOO_LARGE');
    memoryState = valid;
    if (!canUseStorage()) return { persisted: false, state: valid };
    try {
      window.localStorage.setItem(STORAGE_KEY, serialized);
      return { persisted: true, state: valid };
    } catch (_) {
      storageUsable = false;
      return { persisted: false, state: valid };
    }
  }

  function productSnapshotToItem(snapshot) {
    snapshot = isObject(snapshot) ? snapshot : {};
    var item = validateItem({
      client_uuid: snapshot.client_uuid,
      category_key: snapshot.category_key || 'uncategorized',
      kind: 'shopify_variant',
      state: snapshot.state || 'considering',
      priority: snapshot.priority || 'normal',
      quantity: snapshot.quantity || 1,
      shopify_product_gid: snapshot.product_gid || snapshot.shopify_product_gid || snapshot.product_id,
      shopify_variant_gid: snapshot.variant_gid || snapshot.shopify_variant_gid || snapshot.variant_id,
      product_handle_snapshot: snapshot.product_handle || snapshot.handle,
      product_url_snapshot: snapshot.product_url || snapshot.url,
      vendor_snapshot: snapshot.vendor,
      title_snapshot: snapshot.title,
      sku_snapshot: snapshot.sku,
      mpn_snapshot: snapshot.mpn,
      image_url_snapshot: snapshot.image_url || snapshot.image,
      price_snapshot: snapshot.price,
      compare_at_price_snapshot: snapshot.compare_at_price,
      availability_snapshot: snapshot.availability,
      inventory_quantity_snapshot: snapshot.inventory_quantity,
      special_order_snapshot: snapshot.special_order,
      availability_checked_at: snapshot.availability_checked_at,
      currency_snapshot: snapshot.currency,
      customer_note: snapshot.note,
      created_at: now(),
      updated_at: now()
    }, readState().items.length);
    if (!item) throw new Error('INVALID_PRODUCT_SNAPSHOT');
    return item;
  }

  function saveProduct(snapshot) {
    try {
      if (!canUseStorage() && !roots.length) {
        return { ok: false, code: 'STORAGE_UNAVAILABLE', message: 'Local Garage storage is unavailable.' };
      }
      var state = readState();
      if (state.items.length >= MAX_ITEMS) {
        return { ok: false, code: 'ITEM_LIMIT', message: 'This guest project has reached its 100-item limit.' };
      }
      var item = productSnapshotToItem(snapshot);
      var duplicate = state.items.find(function (existing) {
        return existing.kind === 'shopify_variant' &&
          existing.shopify_variant_gid === item.shopify_variant_gid &&
          existing.category_key === item.category_key;
      });
      if (duplicate) {
        return { ok: true, code: 'ALREADY_SAVED', item: duplicate, persisted: canUseStorage() };
      }
      item.position = state.items.length;
      state.items.push(item);
      var result = writeState(state);
      renderAll('Product saved to your local project.');
      return { ok: true, code: 'SAVED', item: item, persisted: result.persisted };
    } catch (error) {
      return {
        ok: false,
        code: error && error.message ? error.message : 'SAVE_FAILED',
        message: 'This product could not be saved to the local Garage.'
      };
    }
  }

  /*
   * Stable PDP/app-block contract:
   * window.AllianceGarage.saveProduct(snapshot) -> result (synchronous)
   *
   * Required snapshot fields:
   *   title
   *   variant_id OR variant_gid/shopify_variant_gid
   *
   * Optional allowlisted fields:
   *   client_uuid, category_key, state, priority, quantity,
   *   product_id/product_gid/shopify_product_gid,
   *   product_handle/handle, product_url/url, vendor, sku, mpn,
   *   image_url/image, price, compare_at_price, availability,
   *   inventory_quantity, special_order, availability_checked_at, currency, note
   *
   * Event alternative:
   * document.dispatchEvent(new CustomEvent('alliance:garage:save-product', {
   *   detail: { request_id: 'opaque-caller-id', snapshot: { ... } }
   * }));
   * Result event:
   * 'alliance:garage:save-product-result' with { request_id, result }.
   */
  window.AllianceGarage = Object.assign(window.AllianceGarage || {}, {
    schemaVersion: SCHEMA_VERSION,
    storageKey: STORAGE_KEY,
    saveProduct: saveProduct,
    getGuestProject: function () {
      return JSON.parse(JSON.stringify(readState()));
    }
  });

  document.addEventListener('alliance:garage:save-product', function (event) {
    var detail = isObject(event.detail) ? event.detail : {};
    var result = saveProduct(detail.snapshot);
    document.dispatchEvent(new CustomEvent('alliance:garage:save-product-result', {
      detail: { request_id: cleanText(detail.request_id, 100), result: result }
    }));
  });

  function parseConfig(root) {
    var node = root.querySelector('[data-garage-config]');
    try {
      return JSON.parse(node ? node.textContent : '{}');
    } catch (_) {
      return {};
    }
  }

  function fallbackCategories() {
    return [
      { key: 'maintenance_reliability', title: 'Maintenance & reliability', description: '' },
      { key: 'handling_suspension', title: 'Handling & suspension', description: '' },
      { key: 'braking', title: 'Braking', description: '' },
      { key: 'wheels_tires', title: 'Wheels & tires', description: '' },
      { key: 'power_airflow', title: 'Power & airflow', description: '' },
      { key: 'cooling', title: 'Cooling', description: '' },
      { key: 'drivetrain', title: 'Drivetrain', description: '' },
      { key: 'safety_track', title: 'Safety & track prep', description: '' },
      { key: 'interior_cockpit', title: 'Interior & cockpit', description: '' },
      { key: 'exterior_aero', title: 'Exterior & aero', description: '' },
      { key: 'uncategorized', title: 'Uncategorized', description: '' }
    ];
  }

  function normalizeCategories(raw) {
    var seen = {};
    var categories = (Array.isArray(raw) ? raw : []).map(function (category) {
      return {
        key: cleanKey(category.key || category.title),
        title: cleanText(category.title, 80),
        description: cleanText(category.description, 240)
      };
    }).filter(function (category) {
      if (!category.title || seen[category.key]) return false;
      seen[category.key] = true;
      return true;
    });
    if (!categories.length) categories = fallbackCategories();
    if (!categories.some(function (category) { return category.key === 'uncategorized'; })) {
      categories.push({ key: 'uncategorized', title: 'Uncategorized', description: 'Ideas and parts that still need a home.' });
    }
    return categories;
  }

  function announce(context, message) {
    var status = context.root.querySelector('[data-garage-status]');
    if (!status) return;
    status.textContent = '';
    window.requestAnimationFrame(function () { status.textContent = message; });
  }

  function showError(context, heading, body, focusSummary) {
    var error = context.root.querySelector('[data-garage-error]');
    if (!error) return;
    error.replaceChildren();
    var title = document.createElement('strong');
    title.textContent = heading;
    var copy = document.createElement('p');
    copy.textContent = body;
    error.append(title, copy);
    error.hidden = false;
    if (focusSummary === false) return;
    error.focus({ preventScroll: true });
    error.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      block: 'center'
    });
  }

  function clearError(context) {
    var error = context.root.querySelector('[data-garage-error]');
    if (error) {
      error.hidden = true;
      error.replaceChildren();
    }
    context.root.querySelectorAll('[aria-invalid="true"]').forEach(function (field) {
      field.removeAttribute('aria-invalid');
      var describedBy = (field.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
      if (error && error.id) describedBy = describedBy.filter(function (id) { return id !== error.id; });
      if (describedBy.length) field.setAttribute('aria-describedby', describedBy.join(' '));
      else field.removeAttribute('aria-describedby');
    });
  }

  function clearFieldError(context, field) {
    var error = context.root.querySelector('[data-garage-error]');
    field.removeAttribute('aria-invalid');
    var describedBy = (field.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
    if (error && error.id) describedBy = describedBy.filter(function (id) { return id !== error.id; });
    if (describedBy.length) field.setAttribute('aria-describedby', describedBy.join(' '));
    else field.removeAttribute('aria-describedby');
  }

  function value(form, name) {
    var field = form.elements.namedItem(name);
    return field ? field.value : '';
  }

  function setValue(form, name, next) {
    var field = form.elements.namedItem(name);
    if (field) field.value = next == null ? '' : next;
  }

  function categoryFor(context, key) {
    return context.categories.find(function (category) { return category.key === key; }) ||
      context.categories.find(function (category) { return category.key === 'uncategorized'; }) ||
      context.categories[0];
  }

  function stateLabel(state) {
    return {
      considering: 'Considering',
      planned: 'Planned',
      ready_to_buy: 'Ready to buy',
      ordered: 'Ordered',
      installed: 'Installed',
      needs_support: 'Needs support'
    }[state] || 'Considering';
  }

  function createOption(value, label, selected) {
    var option = document.createElement('option');
    option.value = value;
    option.textContent = label;
    option.selected = value === selected;
    return option;
  }

  function productLink(item) {
    var url = cleanUrl(item.product_url_snapshot, true);
    if (!url && item.product_handle_snapshot) url = '/products/' + item.product_handle_snapshot;
    if (!url) return null;
    var link = document.createElement('a');
    link.href = url;
    link.textContent = 'View saved product';
    return link;
  }

  function formatItemMoney(item, amount) {
    if (amount == null) return '';
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: item.currency_snapshot || 'USD'
      }).format(amount);
    } catch (_) {
      return '$' + Number(amount).toFixed(2);
    }
  }

  function availabilityLabel(item) {
    if (item.availability_snapshot === 'unavailable') return 'Currently unavailable';
    if (item.special_order_snapshot && Number(item.inventory_quantity_snapshot) <= 0) return 'Special order';
    if (item.availability_snapshot === 'available') {
      var inventory = Number(item.inventory_quantity_snapshot);
      if (Number.isFinite(inventory) && inventory > 0 && inventory <= 10) return 'Only ' + inventory + ' left';
      return 'Available to order';
    }
    return 'Checking availability';
  }

  function checkedLabel(item) {
    if (!item.availability_checked_at) return 'Live product check pending';
    var checked = new Date(item.availability_checked_at).getTime();
    if (!Number.isFinite(checked)) return 'Product data refreshed';
    var minutes = Math.max(0, Math.floor((Date.now() - checked) / 60000));
    if (minutes < 1) return 'Checked just now';
    if (minutes < 60) return 'Checked ' + minutes + 'm ago';
    return 'Checked today';
  }

  function fetchProductSnapshot(item) {
    var handle = item.product_handle_snapshot;
    var variantId = numericId(item.shopify_variant_gid, 'ProductVariant');
    if (!handle || !variantId) return Promise.reject(new Error('MISSING_PRODUCT_REFERENCE'));
    return window.fetch('/products/' + encodeURIComponent(handle) + '?variant=' + encodeURIComponent(variantId) + '&view=garage-data', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    }).then(function (response) {
      if (!response.ok) throw new Error('PRODUCT_NOT_FOUND');
      return response.text();
    }).then(function (body) {
      return JSON.parse(body);
    }).then(function (variant) {
      if (String(variant.variant_id) !== String(variantId)) throw new Error('VARIANT_NOT_FOUND');
      return {
        sku_snapshot: cleanText(variant.sku, 100),
        mpn_snapshot: cleanText(variant.mpn, 100),
        image_url_snapshot: cleanUrl(variant.image, false) || item.image_url_snapshot,
        price_snapshot: cleanMoney(Number(variant.price || 0) / 100),
        compare_at_price_snapshot: cleanMoney(Number(variant.compare_at_price || 0) / 100),
        availability_snapshot: variant.available === false ? 'unavailable' : 'available',
        inventory_quantity_snapshot: cleanInteger(variant.inventory_quantity, -1000000, 1000000, null),
        special_order_snapshot: variant.special_order === true,
        currency_snapshot: cleanCurrency(variant.currency),
        availability_checked_at: now()
      };
    }).catch(function () {
      return window.fetch('/products/' + encodeURIComponent(handle) + '.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      }).then(function (response) {
        if (!response.ok) throw new Error('PRODUCT_NOT_FOUND');
        return response.json();
      }).then(function (product) {
        var variant = (product.variants || []).find(function (entry) { return String(entry.id) === String(variantId); });
        if (!variant) throw new Error('VARIANT_NOT_FOUND');
        var image = variant.featured_image;
        if (image && typeof image === 'object') image = image.src || image.url;
        return {
          sku_snapshot: cleanText(variant.sku, 100),
          mpn_snapshot: item.mpn_snapshot === item.sku_snapshot ? '' : item.mpn_snapshot,
          image_url_snapshot: cleanUrl(image, false) || item.image_url_snapshot,
          price_snapshot: cleanMoney(Number(variant.price || 0) / 100),
          compare_at_price_snapshot: cleanMoney(Number(variant.compare_at_price || 0) / 100),
          availability_snapshot: variant.available === false ? 'unavailable' : 'available',
          inventory_quantity_snapshot: null,
          special_order_snapshot: item.special_order_snapshot,
          availability_checked_at: now()
        };
      });
    });
  }

  function refreshProductSnapshots(context) {
    if (context.refreshingProducts) return;
    var productItems = context.state.items.filter(function (item) {
      return item.kind === 'shopify_variant' && item.product_handle_snapshot && item.shopify_variant_gid;
    });
    if (!productItems.length) return;
    context.refreshingProducts = true;
    Promise.allSettled(productItems.map(function (item) {
      return fetchProductSnapshot(item).then(function (snapshot) {
        Object.keys(snapshot).forEach(function (key) { item[key] = snapshot[key]; });
        item.updated_at = now();
      });
    })).then(function (results) {
      var refreshed = results.filter(function (result) { return result.status === 'fulfilled'; }).length;
      if (refreshed) {
        writeState(context.state);
        renderContext(context, refreshed === 1 ? 'Product price and availability refreshed.' : refreshed + ' products refreshed.');
      }
    }).catch(function () {
      // Saved snapshots remain usable when live product data is temporarily unavailable.
    }).finally(function () {
      context.refreshingProducts = false;
    });
  }

  function createItemCard(context, item, itemIndex, categoryPosition, categoryLength) {
    var card = document.createElement('article');
    card.className = 'garage-item garage-item--' + item.state;
    card.dataset.itemId = item.client_uuid;
    card.dataset.itemLifecycle = item.state;

    var image = null;
    if (item.image_url_snapshot) {
      image = document.createElement('img');
      image.className = 'garage-item__image';
      image.src = item.image_url_snapshot;
      image.alt = '';
      image.width = 96;
      image.height = 96;
      image.loading = 'lazy';
    }

    var top = document.createElement('div');
    top.className = 'garage-item__top';
    var identity = document.createElement('div');
    var eyebrow = document.createElement('p');
    eyebrow.textContent = item.kind === 'shopify_variant'
      ? (item.vendor_snapshot || 'Saved Shopify variant')
      : 'Project task';
    var heading = document.createElement('h4');
    heading.textContent = item.title_snapshot;
    identity.append(eyebrow, heading);
    var badges = document.createElement('div');
    badges.className = 'garage-item__badges';
    var lifecycle = document.createElement('span');
    lifecycle.className = 'garage-item__state';
    lifecycle.textContent = stateLabel(item.state);
    var priority = document.createElement('span');
    priority.className = 'garage-item__priority';
    priority.textContent = item.priority + ' priority';
    badges.append(lifecycle, priority);
    top.append(identity, badges);

    var meta = document.createElement('div');
    meta.className = 'garage-item__meta';
    if (item.mpn_snapshot && item.mpn_snapshot !== item.sku_snapshot) {
      var mpn = document.createElement('span');
      mpn.textContent = 'Part # ' + item.mpn_snapshot;
      meta.appendChild(mpn);
    }
    if (item.quantity > 1) {
      var quantity = document.createElement('span');
      quantity.textContent = 'Qty ' + item.quantity;
      meta.appendChild(quantity);
    }
    var commerce = null;
    if (item.kind === 'shopify_variant') {
      commerce = document.createElement('div');
      commerce.className = 'garage-item__commerce';
      var priceGroup = document.createElement('div');
      priceGroup.className = 'garage-item__price';
      var price = document.createElement('strong');
      price.textContent = item.price_snapshot == null ? 'Price pending' : formatItemMoney(item, item.price_snapshot);
      priceGroup.appendChild(price);
      if (item.compare_at_price_snapshot != null && item.compare_at_price_snapshot > item.price_snapshot) {
        var compareAt = document.createElement('s');
        compareAt.textContent = formatItemMoney(item, item.compare_at_price_snapshot);
        priceGroup.appendChild(compareAt);
      }
      var availability = document.createElement('span');
      availability.className = 'garage-item__availability garage-item__availability--' + item.availability_snapshot;
      availability.textContent = availabilityLabel(item);
      var checked = document.createElement('small');
      checked.textContent = checkedLabel(item);
      commerce.append(priceGroup, availability, checked);

      var commerceActions = document.createElement('div');
      commerceActions.className = 'garage-item__commerce-actions';
      var link = productLink(item);
      if (link) {
        link.textContent = 'View product';
        commerceActions.appendChild(link);
      }
      if (item.state === 'ready_to_buy' && item.availability_snapshot === 'available') {
        var cartForm = document.createElement('form');
        cartForm.action = '/cart/add';
        cartForm.method = 'post';
        var cartVariant = document.createElement('input');
        cartVariant.type = 'hidden';
        cartVariant.name = 'id';
        cartVariant.value = numericId(item.shopify_variant_gid, 'ProductVariant');
        var cartQuantity = document.createElement('input');
        cartQuantity.type = 'hidden';
        cartQuantity.name = 'quantity';
        cartQuantity.value = item.quantity;
        var cartButton = document.createElement('button');
        cartButton.type = 'submit';
        cartButton.textContent = 'Add to cart';
        cartForm.append(cartVariant, cartQuantity, cartButton);
        commerceActions.appendChild(cartForm);
      }
      commerce.appendChild(commerceActions);
    }

    var fitment = document.createElement('p');
    fitment.className = 'garage-item__fitment';
    fitment.textContent = item.kind === 'shopify_variant'
      ? 'Fitment is not verified for your vehicle. Price and availability can change before checkout.'
      : 'Customer-created milestone; no fitment or performance claim.';

    var fields = document.createElement('div');
    fields.className = 'garage-item__fields';

    var stateLabelElement = document.createElement('label');
    var stateText = document.createElement('span');
    stateText.textContent = 'Lifecycle state';
    var stateSelect = document.createElement('select');
    stateSelect.dataset.itemState = item.client_uuid;
    VALID_STATES.forEach(function (state) {
      if (state === 'installed' && !context.config.show_installation_state && item.state !== 'installed') return;
      stateSelect.appendChild(createOption(state, stateLabel(state), item.state));
    });
    stateLabelElement.append(stateText, stateSelect);

    var categoryLabel = document.createElement('label');
    var categoryText = document.createElement('span');
    categoryText.textContent = 'Category';
    var categorySelect = document.createElement('select');
    categorySelect.dataset.itemCategory = item.client_uuid;
    context.categories.forEach(function (category) {
      categorySelect.appendChild(createOption(category.key, category.title, item.category_key));
    });
    categoryLabel.append(categoryText, categorySelect);
    fields.append(stateLabelElement, categoryLabel);

    var noteLabel = document.createElement('label');
    noteLabel.className = 'garage-item__note';
    var noteText = document.createElement('span');
    noteText.textContent = 'Item note';
    var note = document.createElement('textarea');
    note.rows = 2;
    note.maxLength = 1000;
    note.value = item.customer_note;
    note.dataset.itemNote = item.client_uuid;
    noteLabel.append(noteText, note);

    var actions = document.createElement('div');
    actions.className = 'garage-item__actions';
    var up = document.createElement('button');
    up.type = 'button';
    up.className = 'garage-item__move';
    up.dataset.itemMove = item.client_uuid;
    up.dataset.direction = '-1';
    up.textContent = 'Move earlier';
    up.disabled = categoryPosition === 0;
    var down = document.createElement('button');
    down.type = 'button';
    down.className = 'garage-item__move';
    down.dataset.itemMove = item.client_uuid;
    down.dataset.direction = '1';
    down.textContent = 'Move later';
    down.disabled = categoryPosition === categoryLength - 1;
    var remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'garage-item__remove';
    remove.dataset.itemRemove = item.client_uuid;
    remove.textContent = 'Remove ' + item.title_snapshot;
    actions.append(up, down, remove);

    var editor = document.createElement('details');
    editor.className = 'garage-item__editor';
    var editorSummary = document.createElement('summary');
    editorSummary.textContent = 'Edit milestone';
    editor.append(editorSummary, fields, noteLabel, actions);

    if (image) card.append(image);
    card.append(top);
    if (meta.childNodes.length) card.append(meta);
    if (commerce) card.append(commerce);
    card.append(fitment, editor);
    return card;
  }

  function renderBoard(context) {
    var board = context.root.querySelector('[data-garage-board]');
    if (!board) return;
    board.replaceChildren();
    var activeCategory = categoryFor(context, context.activeCategoryKey);
    context.activeCategoryKey = activeCategory.key;
    var categoryItems = context.state.items
      .map(function (item, index) { return { item: item, index: index }; })
      .filter(function (entry) { return categoryFor(context, entry.item.category_key).key === activeCategory.key; });
    categoryItems.forEach(function (entry, categoryPosition) {
      entry.categoryPosition = categoryPosition;
    });

    var boardTitle = context.root.querySelector('[data-garage-board-title]');
    if (boardTitle) boardTitle.textContent = activeCategory.title + ' plan';
    context.root.querySelectorAll('[data-garage-system-key]').forEach(function (control) {
      var isActive = control.dataset.garageSystemKey === activeCategory.key;
      control.classList.toggle('is-active', isActive);
      if (isActive) control.setAttribute('aria-current', 'true');
      else control.removeAttribute('aria-current');
    });

    var laneDefinitions = [
      { key: 'considering', title: 'Considering', help: 'Ideas and parts still being evaluated.' },
      { key: 'planned', title: 'Planned', help: 'Committed, ready-to-buy, ordered, or support-needed work.' }
    ];
    if (context.config.show_installation_state) {
      laneDefinitions.push({ key: 'installed', title: 'Installed', help: 'Completed milestones already on the vehicle.' });
    }

    laneDefinitions.forEach(function (lane) {
      var section = document.createElement('section');
      section.className = 'garage-lane garage-lane--' + lane.key;
      section.setAttribute('aria-label', lane.title + ' milestones');
      var heading = document.createElement('div');
      heading.className = 'garage-lane__heading';
      var title = document.createElement('h3');
      title.textContent = lane.title;
      var items = categoryItems.filter(function (entry) {
        if (lane.key === 'considering') return entry.item.state === 'considering';
        if (lane.key === 'installed') return entry.item.state === 'installed';
        return entry.item.state !== 'considering' && (!context.config.show_installation_state || entry.item.state !== 'installed');
      });
      var count = document.createElement('span');
      count.textContent = String(items.length);
      heading.append(title, count);
      var list = document.createElement('div');
      list.className = 'garage-lane__items';
      if (!items.length) {
        var empty = document.createElement('p');
        empty.className = 'garage-lane__empty';
        empty.textContent = lane.help;
        list.appendChild(empty);
      } else {
        items.forEach(function (entry) {
          list.appendChild(createItemCard(
            context,
            entry.item,
            entry.index,
            entry.categoryPosition,
            categoryItems.length
          ));
        });
      }
      section.append(heading, list);
      board.appendChild(section);
    });
  }

  function renderSummary(context) {
    var state = context.state;
    var vehicleName = [state.vehicle.year, state.vehicle.make, state.vehicle.model, state.vehicle.trim].filter(Boolean).join(' ');
    var title = context.root.querySelector('[data-garage-summary-title]');
    var vehicle = context.root.querySelector('[data-garage-vehicle-summary]');
    var total = context.root.querySelector('[data-garage-total]');
    var installed = context.root.querySelector('[data-garage-installed]');
    var progress = context.root.querySelector('[data-garage-progress]');
    var next = context.root.querySelector('[data-garage-next]');
    title.textContent = state.build.name || 'Street build';
    vehicle.textContent = vehicleName || 'Add your vehicle details to make this Garage yours.';
    total.textContent = state.items.length;
    var plannedItems = state.items.filter(function (item) { return item.state !== 'considering'; });
    var installedCount = plannedItems.filter(function (item) { return item.state === 'installed'; }).length;
    var consideringCount = state.items.filter(function (item) { return item.state === 'considering'; }).length;
    var activePlanCount = state.items.filter(function (item) {
      return item.state !== 'considering' && item.state !== 'installed';
    }).length;
    var needsSupportCount = state.items.filter(function (item) { return item.state === 'needs_support'; }).length;
    var progressPercent = plannedItems.length ? Math.round((installedCount / plannedItems.length) * 100) : 0;
    if (installed) installed.textContent = installedCount;
    progress.textContent = context.config.show_installation_state
      ? installedCount + ' of ' + plannedItems.length +
        (plannedItems.length === 1 ? ' planned milestone' : ' planned milestones') + ' marked installed.'
      : state.items.length + (state.items.length === 1 ? ' milestone tracked.' : ' milestones tracked.');
    var nextItem = state.items.find(function (item) { return item.state === 'ready_to_buy'; }) ||
      state.items.find(function (item) { return item.state === 'planned'; }) ||
      state.items.find(function (item) { return item.state === 'considering'; }) ||
      state.items.find(function (item) { return item.state === 'needs_support'; });
    next.textContent = nextItem ? nextItem.title_snapshot + ' — ' + stateLabel(nextItem.state) : 'No active milestone selected yet.';
    context.root.querySelectorAll('[data-garage-command-build]').forEach(function (element) {
      element.textContent = state.build.name || 'Street build';
    });
    context.root.querySelectorAll('[data-garage-command-vehicle]').forEach(function (element) {
      element.textContent = vehicleName || 'Add your vehicle';
    });
    context.root.querySelectorAll('[data-garage-command-goal]').forEach(function (element) {
      element.textContent = state.build.goal || 'Define your build goal';
    });
    context.root.querySelectorAll('[data-garage-command-budget]').forEach(function (element) {
      var amount = Number(state.build.budget_amount);
      element.textContent = amount > 0
        ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(amount)
        : 'Not set';
    });
    context.root.querySelectorAll('[data-garage-command-planned]').forEach(function (element) { element.textContent = plannedItems.length; });
    context.root.querySelectorAll('[data-garage-command-considering]').forEach(function (element) { element.textContent = consideringCount; });
    context.root.querySelectorAll('[data-garage-command-active]').forEach(function (element) { element.textContent = activePlanCount; });
    context.root.querySelectorAll('[data-garage-command-installed]').forEach(function (element) { element.textContent = installedCount; });
    context.root.querySelectorAll('[data-garage-command-questions]').forEach(function (element) { element.textContent = needsSupportCount; });
    context.root.querySelectorAll('[data-garage-command-progress]').forEach(function (element) { element.textContent = progressPercent + '%'; });
    context.root.querySelectorAll('[data-garage-command-progress-bar]').forEach(function (element) { element.style.width = progressPercent + '%'; });
    context.root.querySelectorAll('[data-garage-command-progress-meter]').forEach(function (element) {
      element.setAttribute('aria-valuenow', String(progressPercent));
      element.setAttribute('aria-valuetext', progressPercent + '% of planned milestones installed');
    });
    context.root.querySelectorAll('[data-garage-system-key]').forEach(function (element) {
      var categoryKey = element.dataset.garageSystemKey;
      var categoryCount = state.items.filter(function (item) {
        return categoryFor(context, item.category_key).key === categoryKey;
      }).length;
      var countElement = element.querySelector('[data-garage-system-count]');
      if (countElement) countElement.textContent = categoryCount;
    });
    var buildNotes = context.root.querySelector('[data-garage-notes-summary]');
    if (buildNotes) buildNotes.textContent = state.build.draft_note || 'No project notes yet.';
    var supportQuestion = context.root.querySelector('[data-garage-support-question]');
    var questionItem = state.items.find(function (item) { return item.state === 'needs_support'; });
    if (supportQuestion) {
      supportQuestion.textContent = questionItem
        ? (questionItem.customer_note || questionItem.title_snapshot)
        : 'No open fitment questions.';
    }
  }

  function populateForms(context) {
    var state = context.state;
    var vehicleForm = context.root.querySelector('[data-garage-vehicle-form]');
    Object.keys(state.vehicle).forEach(function (name) { setValue(vehicleForm, name, state.vehicle[name]); });
    var buildForm = context.root.querySelector('[data-garage-build-form]');
    Object.keys(state.build).forEach(function (name) { setValue(buildForm, name, state.build[name]); });
  }

  function renderContext(context, message) {
    context.state = readState();
    renderSummary(context);
    renderBoard(context);
    populateForms(context);
    var saveState = context.root.querySelector('[data-garage-save-state]');
    saveState.textContent = canUseStorage()
      ? 'Saved changes stay in this browser.'
      : 'Temporary session only. Export a backup before leaving.';
    var warning = context.root.querySelector('[data-garage-storage-warning]');
    warning.hidden = canUseStorage();
    if (message) announce(context, message);
  }

  function renderAll(message) {
    roots.forEach(function (context) { renderContext(context, message); });
  }

  function persistContext(context, message) {
    try {
      var result = writeState(context.state);
      renderAll(message);
      if (!result.persisted) announce(context, message + ' Browser storage is unavailable; export a backup.');
    } catch (error) {
      showError(context, 'Project could not be saved', error.message === 'PAYLOAD_TOO_LARGE'
        ? 'This local project exceeds the 256 KiB safety limit. Remove notes or items and try again.'
        : 'Review the project information and try again.');
    }
  }

  function invalidForm(context, form, focusSummary) {
    var invalidFields = Array.from(form.querySelectorAll(':invalid'));
    var invalid = invalidFields[0];
    var error = context.root.querySelector('[data-garage-error]');
    invalidFields.forEach(function (field) {
      field.setAttribute('aria-invalid', 'true');
      if (error && error.id) {
        var describedBy = (field.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
        if (describedBy.indexOf(error.id) === -1) describedBy.push(error.id);
        field.setAttribute('aria-describedby', describedBy.join(' '));
      }
    });
    showError(context, 'Check the highlighted field', 'Complete the required information before saving this section. The first invalid field is ' +
      (invalid && invalid.closest('label') ? cleanText(invalid.closest('label').textContent, 80) : 'highlighted below') + '.', focusSummary);
  }

  function updateInvalidForm(context, form, event) {
    if (!event.target.matches('[aria-invalid="true"]') || !event.target.checkValidity()) return;
    clearFieldError(context, event.target);
    if (form.checkValidity()) clearError(context);
    else invalidForm(context, form, false);
  }

  function bindForms(context) {
    var vehicleForm = context.root.querySelector('[data-garage-vehicle-form]');
    vehicleForm.noValidate = true;
    vehicleForm.addEventListener('input', function (event) {
      updateInvalidForm(context, vehicleForm, event);
    });
    vehicleForm.addEventListener('submit', function (event) {
      event.preventDefault();
      clearError(context);
      if (!vehicleForm.checkValidity()) {
        invalidForm(context, vehicleForm);
        return;
      }
      var current = context.state.vehicle;
      context.state.vehicle = validateVehicle({
        client_uuid: current.client_uuid,
        nickname: value(vehicleForm, 'nickname'),
        year: value(vehicleForm, 'year'),
        make: value(vehicleForm, 'make'),
        model: value(vehicleForm, 'model'),
        trim: value(vehicleForm, 'trim'),
        engine: value(vehicleForm, 'engine'),
        drivetrain: value(vehicleForm, 'drivetrain')
      });
      persistContext(context, 'Vehicle details saved.');
    });

    var buildForm = context.root.querySelector('[data-garage-build-form]');
    buildForm.noValidate = true;
    buildForm.addEventListener('input', function (event) {
      updateInvalidForm(context, buildForm, event);
    });
    buildForm.addEventListener('submit', function (event) {
      event.preventDefault();
      clearError(context);
      if (!buildForm.checkValidity()) {
        invalidForm(context, buildForm);
        return;
      }
      var current = context.state.build;
      context.state.build = validateBuild({
        client_uuid: current.client_uuid,
        name: value(buildForm, 'name'),
        purpose: value(buildForm, 'purpose'),
        goal: value(buildForm, 'goal'),
        budget_amount: value(buildForm, 'budget_amount'),
        budget_currency: current.budget_currency,
        draft_note: value(buildForm, 'draft_note')
      });
      persistContext(context, 'Build plan saved.');
    });

    var itemForm = context.root.querySelector('[data-garage-item-form]');
    itemForm.noValidate = true;
    itemForm.addEventListener('input', function (event) {
      updateInvalidForm(context, itemForm, event);
    });
    var kind = itemForm.elements.namedItem('kind');
    var productFields = context.root.querySelector('[data-garage-product-fields]');
    var taskTitle = context.root.querySelector('[data-garage-task-title]');
    var titleField = itemForm.elements.namedItem('title_snapshot');
    var productLookup = context.root.querySelector('[data-garage-product-lookup]');
    var productFind = context.root.querySelector('[data-garage-product-find]');
    var productStatus = context.root.querySelector('[data-garage-product-status]');
    var productResults = context.root.querySelector('[data-garage-product-results]');
    var productSelected = context.root.querySelector('[data-garage-product-selected]');
    var productImage = context.root.querySelector('[data-garage-product-image]');
    var productTitle = context.root.querySelector('[data-garage-product-title]');
    var productMeta = context.root.querySelector('[data-garage-product-meta]');
    var variantLabel = context.root.querySelector('[data-garage-variant-label]');
    var variantSelect = context.root.querySelector('[data-garage-variant-select]');
    var selectedProduct = null;

    function productImageUrl(product, variant) {
      var variantImage = variant && variant.featured_image;
      if (variantImage && typeof variantImage === 'object') return variantImage.src || variantImage.url || '';
      if (typeof variantImage === 'string') return variantImage;
      var featured = product && product.featured_image;
      if (featured && typeof featured === 'object') return featured.src || featured.url || '';
      return typeof featured === 'string' ? featured : '';
    }

    function selectVariant(variantId) {
      if (!selectedProduct) return;
      var variant = selectedProduct.variants.find(function (entry) { return String(entry.id) === String(variantId); }) ||
        selectedProduct.variants[0];
      if (!variant) return;
      variantSelect.value = String(variant.id);
      var optionTitle = variant.title && variant.title !== 'Default Title' ? ' — ' + variant.title : '';
      var image = productImageUrl(selectedProduct, variant);
      setValue(itemForm, 'product_id', selectedProduct.id);
      setValue(itemForm, 'variant_id', variant.id);
      setValue(itemForm, 'product_url', '/products/' + selectedProduct.handle + '?variant=' + variant.id);
      setValue(itemForm, 'vendor_snapshot', selectedProduct.vendor);
      setValue(itemForm, 'title_snapshot', selectedProduct.title + optionTitle);
      setValue(itemForm, 'sku_snapshot', variant.sku);
      setValue(itemForm, 'mpn_snapshot', '');
      setValue(itemForm, 'image_url_snapshot', image);
      setValue(itemForm, 'price_snapshot', Number(variant.price || 0) / 100);
      setValue(itemForm, 'compare_at_price_snapshot', Number(variant.compare_at_price || 0) / 100);
      setValue(itemForm, 'availability_snapshot', variant.available === false ? 'unavailable' : 'available');
      setValue(itemForm, 'inventory_quantity_snapshot', '');
      setValue(itemForm, 'availability_checked_at', now());
      productTitle.textContent = selectedProduct.title + optionTitle;
      productMeta.textContent = [selectedProduct.vendor, variant.sku ? 'SKU ' + variant.sku : '', variant.available === false ? 'Currently unavailable' : 'Available to select']
        .filter(Boolean).join(' · ');
      if (image) {
        productImage.src = image;
        productImage.hidden = false;
      } else {
        productImage.removeAttribute('src');
        productImage.hidden = true;
      }
    }

    function showSelectedProduct(product) {
      if (!product || !Array.isArray(product.variants) || !product.variants.length) {
        throw new Error('NO_VARIANTS');
      }
      selectedProduct = product;
      productResults.hidden = true;
      productResults.replaceChildren();
      variantSelect.replaceChildren();
      product.variants.forEach(function (variant) {
        var option = document.createElement('option');
        option.value = variant.id;
        option.textContent = (variant.title && variant.title !== 'Default Title' ? variant.title : 'Standard option') +
          (variant.sku ? ' — ' + variant.sku : '') + (variant.available === false ? ' — unavailable' : '');
        option.disabled = variant.available === false;
        variantSelect.appendChild(option);
      });
      var firstAvailable = product.variants.find(function (variant) { return variant.available !== false; }) || product.variants[0];
      variantLabel.hidden = product.variants.length < 2;
      productSelected.hidden = false;
      selectVariant(firstAvailable.id);
      productStatus.textContent = 'Product found. Review the selected option, then add it to your project.';
    }

    function clearSelectedProduct(clearLookup) {
      selectedProduct = null;
      productSelected.hidden = true;
      productResults.hidden = true;
      productResults.replaceChildren();
      variantSelect.replaceChildren();
      ['product_url', 'product_id', 'variant_id', 'vendor_snapshot', 'sku_snapshot', 'mpn_snapshot', 'image_url_snapshot', 'price_snapshot',
        'compare_at_price_snapshot', 'availability_snapshot', 'inventory_quantity_snapshot', 'availability_checked_at']
        .forEach(function (name) { setValue(itemForm, name, ''); });
      setValue(itemForm, 'title_snapshot', '');
      if (clearLookup) productLookup.value = '';
      productStatus.textContent = '';
    }

    function loadProduct(productUrl) {
      var handle = productHandleFrom(productUrl, '');
      if (!handle) return Promise.reject(new Error('INVALID_PRODUCT_URL'));
      return window.fetch('/products/' + encodeURIComponent(handle) + '.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      }).then(function (response) {
        if (!response.ok) throw new Error('PRODUCT_NOT_FOUND');
        return response.json();
      }).then(function (product) {
        product.handle = product.handle || handle;
        showSelectedProduct(product);
      });
    }

    function renderProductResults(products) {
      productResults.replaceChildren();
      products.forEach(function (product) {
        var button = document.createElement('button');
        button.type = 'button';
        button.className = 'garage-product-result';
        var image = document.createElement('img');
        var imageSource = product.featured_image && (product.featured_image.url || product.featured_image.src);
        image.alt = '';
        if (imageSource) image.src = imageSource;
        var copy = document.createElement('span');
        var title = document.createElement('strong');
        title.textContent = product.title;
        var vendor = document.createElement('span');
        vendor.textContent = product.vendor || 'Alliance Racing';
        copy.append(title, vendor);
        var action = document.createElement('b');
        action.textContent = 'Select';
        button.append(image, copy, action);
        button.addEventListener('click', function () {
          productStatus.textContent = 'Loading product options…';
          loadProduct(product.url).catch(function () {
            productStatus.textContent = 'That product could not be loaded. Open its product page or try another result.';
          });
        });
        productResults.appendChild(button);
      });
      productResults.hidden = false;
    }

    function findProduct() {
      var query = cleanText(productLookup.value, 500);
      if (!query) {
        productStatus.textContent = 'Enter a part number, SKU, or Alliance product URL.';
        productLookup.focus();
        return;
      }
      clearSelectedProduct(false);
      productFind.disabled = true;
      productStatus.textContent = 'Searching the Alliance catalog…';
      var request;
      if (query.indexOf('/products/') > -1) {
        request = loadProduct(query);
      } else {
        var searchUrl = '/search/suggest.json?q=' + encodeURIComponent(query) +
          '&resources[type]=product&resources[limit]=8&resources[options][fields]=title,vendor,variants.sku,product_type';
        request = window.fetch(searchUrl, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' }
        }).then(function (response) {
          if (!response.ok) throw new Error('SEARCH_FAILED');
          return response.json();
        }).then(function (payload) {
          var products = payload && payload.resources && payload.resources.results
            ? payload.resources.results.products || []
            : [];
          if (!products.length) {
            productStatus.textContent = 'No published products matched that part number. Check the number or ask the Parts Desk.';
            return;
          }
          productStatus.textContent = products.length + (products.length === 1 ? ' matching product.' : ' matching products. Choose one below.');
          renderProductResults(products);
        });
      }
      request.catch(function () {
        productStatus.textContent = 'We could not load that product. Use an Alliance product URL or try the part number again.';
      }).finally(function () {
        productFind.disabled = false;
      });
    }

    function updateKind() {
      var product = kind.value === 'shopify_variant';
      productFields.hidden = !product;
      taskTitle.hidden = product;
      titleField.required = !product;
      if (product) productLookup.focus();
    }
    kind.addEventListener('change', updateKind);
    productFind.addEventListener('click', findProduct);
    productLookup.addEventListener('keydown', function (event) {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      findProduct();
    });
    variantSelect.addEventListener('change', function () { selectVariant(variantSelect.value); });
    context.root.querySelector('[data-garage-product-clear]').addEventListener('click', function () {
      clearSelectedProduct(true);
      productLookup.focus();
    });
    updateKind();

    itemForm.addEventListener('submit', function (event) {
      event.preventDefault();
      clearError(context);
      if (!itemForm.checkValidity()) {
        invalidForm(context, itemForm);
        return;
      }
      if (context.state.items.length >= MAX_ITEMS) {
        showError(context, 'Project item limit reached', 'A guest project can contain up to 100 milestones.');
        return;
      }
      if (kind.value === 'shopify_variant' && !value(itemForm, 'variant_id')) {
        showError(context, 'Choose a catalog product', 'Search by part number or paste an Alliance product URL, then select the product before adding it.');
        productLookup.focus();
        return;
      }
      var raw = {
        client_uuid: uuid(),
        kind: value(itemForm, 'kind'),
        category_key: value(itemForm, 'category_key'),
        state: 'considering',
        priority: value(itemForm, 'priority'),
        quantity: value(itemForm, 'quantity'),
        position: context.state.items.length,
        title_snapshot: value(itemForm, 'title_snapshot'),
        product_url: value(itemForm, 'product_url'),
        product_id: value(itemForm, 'product_id'),
        variant_id: value(itemForm, 'variant_id'),
        vendor_snapshot: value(itemForm, 'vendor_snapshot'),
        sku_snapshot: value(itemForm, 'sku_snapshot'),
        mpn_snapshot: value(itemForm, 'mpn_snapshot'),
        image_url_snapshot: value(itemForm, 'image_url_snapshot'),
        price_snapshot: value(itemForm, 'price_snapshot'),
        compare_at_price_snapshot: value(itemForm, 'compare_at_price_snapshot'),
        availability_snapshot: value(itemForm, 'availability_snapshot'),
        inventory_quantity_snapshot: value(itemForm, 'inventory_quantity_snapshot'),
        availability_checked_at: value(itemForm, 'availability_checked_at'),
        customer_note: value(itemForm, 'customer_note'),
        created_at: now(),
        updated_at: now()
      };
      var item = validateItem(raw, context.state.items.length);
      if (!item) {
        showError(context, 'Milestone could not be added', 'Select a published Alliance product or enter a title for a task.');
        return;
      }
      context.state.items.push(item);
      context.activeCategoryKey = item.category_key;
      persistContext(context, item.title_snapshot + ' added to the project.');
      itemForm.reset();
      kind.value = 'task';
      clearSelectedProduct(true);
      updateKind();
      context.root.querySelector('[data-garage-kind]').focus();
      refreshProductSnapshots(context);
    });
  }

  function itemById(context, id) {
    return context.state.items.find(function (item) { return item.client_uuid === id; });
  }

  function bindBoard(context) {
    var board = context.root.querySelector('[data-garage-board]');
    board.addEventListener('change', function (event) {
      var stateControl = event.target.closest('[data-item-state]');
      var categoryControl = event.target.closest('[data-item-category]');
      var noteControl = event.target.closest('[data-item-note]');
      var control = stateControl || categoryControl || noteControl;
      if (!control) return;
      var id = control.dataset.itemState || control.dataset.itemCategory || control.dataset.itemNote;
      var item = itemById(context, id);
      if (!item) return;
      if (stateControl && VALID_STATES.indexOf(stateControl.value) > -1) item.state = stateControl.value;
      if (categoryControl) {
        item.category_key = categoryFor(context, categoryControl.value).key;
        context.activeCategoryKey = item.category_key;
      }
      if (noteControl) item.customer_note = cleanText(noteControl.value, 1000);
      item.updated_at = now();
      persistContext(context, item.title_snapshot + ' updated.');
      var equivalent = context.root.querySelector(
        stateControl ? '[data-item-state="' + id + '"]' :
          categoryControl ? '[data-item-category="' + id + '"]' :
            '[data-item-note="' + id + '"]'
      );
      if (equivalent) equivalent.focus({ preventScroll: true });
    });

    board.addEventListener('click', function (event) {
      var remove = event.target.closest('[data-item-remove]');
      var move = event.target.closest('[data-item-move]');
      if (!remove && !move) return;
      var id = remove ? remove.dataset.itemRemove : move.dataset.itemMove;
      var index = context.state.items.findIndex(function (item) { return item.client_uuid === id; });
      if (index < 0) return;
      if (remove) {
        var itemTitle = context.state.items[index].title_snapshot;
        if (!window.confirm('Remove “' + itemTitle + '” from this local project?')) return;
        context.state.items.splice(index, 1);
        persistContext(context, itemTitle + ' removed from the project.');
        var boardTitle = context.root.querySelector('[id^="GarageBoardTitle-"]');
        if (boardTitle) {
          boardTitle.tabIndex = -1;
          boardTitle.focus({ preventScroll: true });
        }
        return;
      }
      var currentCategory = categoryFor(context, context.state.items[index].category_key).key;
      var categoryIndices = context.state.items
        .map(function (item, itemIndex) {
          return categoryFor(context, item.category_key).key === currentCategory ? itemIndex : -1;
        })
        .filter(function (itemIndex) { return itemIndex > -1; });
      var categoryIndex = categoryIndices.indexOf(index);
      var nextCategoryIndex = categoryIndex + Number(move.dataset.direction);
      if (nextCategoryIndex < 0 || nextCategoryIndex >= categoryIndices.length) return;
      var nextIndex = categoryIndices[nextCategoryIndex];
      var moved = context.state.items[index];
      context.state.items[index] = context.state.items[nextIndex];
      context.state.items[nextIndex] = moved;
      context.state.items.forEach(function (item, position) { item.position = position; });
      persistContext(context, moved.title_snapshot + ' reordered.');
      var equivalent = context.root.querySelector('[data-item-move="' + id + '"][data-direction="' + move.dataset.direction + '"]');
      if (equivalent) equivalent.focus({ preventScroll: true });
    });
  }

  function bindSystemRail(context) {
    context.root.querySelectorAll('[data-garage-system-key]').forEach(function (control) {
      control.addEventListener('click', function () {
        context.activeCategoryKey = categoryFor(context, control.dataset.garageSystemKey).key;
        renderBoard(context);
      });
    });
  }

  function downloadExport(context) {
    var bundle = {
      format: 'alliance.garage.export',
      export_version: 1,
      exported_at: now(),
      garage: validateState(context.state)
    };
    var blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' });
    var url = URL.createObjectURL(blob);
    var anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'alliance-garage-' + new Date().toISOString().slice(0, 10) + '.json';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
    announce(context, 'Project backup exported.');
  }

  function openDialog(dialog) {
    if (!dialog || typeof dialog.showModal !== 'function') return;
    dialog.showModal();
    var title = dialog.querySelector('h2');
    if (title) window.requestAnimationFrame(function () { title.focus(); });
  }

  function bindDataTools(context) {
    var root = context.root;
    var resetDialog = root.querySelector('[data-garage-reset-dialog]');
    var importDialog = root.querySelector('[data-garage-import-dialog]');
    var importInput = root.querySelector('[data-garage-import]');
    var stagedImport = null;

    root.querySelector('[data-garage-export]').addEventListener('click', function () {
      clearError(context);
      downloadExport(context);
    });
    root.querySelector('[data-garage-reset-open]').addEventListener('click', function () {
      openDialog(resetDialog);
    });
    root.querySelectorAll('[data-garage-dialog-close]').forEach(function (button) {
      button.addEventListener('click', function () { button.closest('dialog').close(); });
    });
    root.querySelector('[data-garage-reset-confirm]').addEventListener('click', function () {
      if (canUseStorage()) window.localStorage.removeItem(STORAGE_KEY);
      memoryState = defaultState();
      resetDialog.close();
      renderAll('Local project reset.');
      var heading = root.querySelector('[data-garage-project-heading]');
      if (heading) {
        heading.tabIndex = -1;
        heading.focus({ preventScroll: true });
      }
    });

    importInput.addEventListener('change', function () {
      clearError(context);
      var file = importInput.files && importInput.files[0];
      if (!file) return;
      if (file.size > MAX_BYTES) {
        showError(context, 'Backup is too large', 'Garage backup files must be 256 KiB or smaller.');
        importInput.value = '';
        return;
      }
      file.text().then(function (contents) {
        var parsed = JSON.parse(contents);
        var payload = isObject(parsed) && parsed.format === 'alliance.garage.export' ? parsed.garage : parsed;
        if (isObject(parsed) && parsed.format && parsed.format !== 'alliance.garage.export') throw new Error('UNKNOWN_EXPORT_FORMAT');
        if (isObject(parsed) && parsed.export_version && Number(parsed.export_version) !== 1) throw new Error('UNKNOWN_EXPORT_VERSION');
        stagedImport = validateState(payload);
        var summary = importDialog.querySelector('[data-garage-import-summary]');
        summary.textContent = 'Backup contains ' + stagedImport.items.length +
          (stagedImport.items.length === 1 ? ' milestone' : ' milestones') + ' for ' +
          ([stagedImport.vehicle.year, stagedImport.vehicle.make, stagedImport.vehicle.model].filter(Boolean).join(' ') || 'an unnamed vehicle') + '.';
        openDialog(importDialog);
      }).catch(function () {
        stagedImport = null;
        showError(context, 'Backup could not be restored', 'Choose an unmodified Alliance Garage v1 JSON export within the supported limits.');
      }).finally(function () {
        importInput.value = '';
      });
    });

    root.querySelector('[data-garage-import-confirm]').addEventListener('click', function () {
      if (!stagedImport) return;
      context.state = stagedImport;
      var result = writeState(context.state);
      stagedImport = null;
      importDialog.close();
      renderAll(result.persisted ? 'Project backup restored.' : 'Backup opened for this session. Browser storage remains unavailable.');
      var heading = root.querySelector('[data-garage-project-heading]');
      if (heading) {
        heading.tabIndex = -1;
        heading.focus({ preventScroll: true });
      }
    });
  }

  function initialize(root) {
    if (!root || root.dataset.garageInitialized === 'true') return;
    root.dataset.garageInitialized = 'true';
    var config = parseConfig(root);
    config.show_budget = config.show_budget !== false;
    config.show_installation_state = config.show_installation_state !== false;
    var normalizedCategories = normalizeCategories(config.categories);
    var currentState = readState();
    var firstSavedCategory = currentState.items.length
      ? categoryFor({ categories: normalizedCategories }, currentState.items[0].category_key).key
      : '';
    var preferredCategory = normalizedCategories.find(function (category) { return category.key === 'handling_suspension'; });
    var context = {
      root: root,
      config: config,
      categories: normalizedCategories,
      state: currentState,
      refreshingProducts: false,
      activeCategoryKey: firstSavedCategory || (preferredCategory && preferredCategory.key) || normalizedCategories[0].key
    };
    roots.push(context);

    root.querySelectorAll('[data-garage-category-select]').forEach(function (select) {
      context.categories.forEach(function (category) {
        select.appendChild(createOption(category.key, category.title, 'uncategorized'));
      });
    });
    bindForms(context);
    bindBoard(context);
    bindSystemRail(context);
    bindDataTools(context);

    var loading = root.querySelector('[data-garage-loading]');
    var workspace = root.querySelector('[data-garage-workspace]');
    if (loading) loading.hidden = true;
    if (workspace) workspace.hidden = false;
    root.classList.add('is-garage-ready');
    renderContext(context);
    refreshProductSnapshots(context);
    if (context.state.storage_error) {
      showError(context, 'Saved Garage could not be read', context.state.storage_error + ' Export/reset only after reviewing this new temporary project.');
    }
    if (new URLSearchParams(window.location.search).get('import') === '1') {
      announce(context, 'Your guest project remains local. Account import is not active yet; export a backup to keep a recovery copy.');
    }
  }

  document.querySelectorAll('[data-garage-page]').forEach(initialize);
  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.matches && event.target.matches('[data-garage-page]')
      ? event.target
      : event.target.querySelector && event.target.querySelector('[data-garage-page]');
    if (root) initialize(root);
  });
  document.addEventListener('shopify:section:unload', function (event) {
    roots = roots.filter(function (context) {
      return context.root !== event.target && !event.target.contains(context.root);
    });
  });
  window.addEventListener('storage', function (event) {
    if (event.key !== STORAGE_KEY) return;
    memoryState = null;
    renderAll('This local project was updated in another tab.');
  });

  document.dispatchEvent(new CustomEvent('alliance:garage:ready', {
    detail: { schema_version: SCHEMA_VERSION, storage_key: STORAGE_KEY }
  }));
})();
